// Comprehensive state-specific family law data
import maineData from './maineData';
import wyomingData from './wyomingData';
import wisconsinData from './wisconsinData';
import westVirginiaData from './westVirginiaData';
import washingtonDCData from './washingtonDCData';
import washingtonStateData from './washingtonStateData';
import virginiaData from './virginiaData';
import vermontData from './vermontData';
import utahData from './utahData';
import texasData from './texasData';
import tennesseeData from './tennesseeData';
import californiaData from './californiaData';
import iowaData from './iowaData';
import arizonaData from './arizonaData';
import coloradoData from './coloradoData';
import arkansasData from './arkansasData';
import connecticutData from './connecticutData';
import delawareData from './delawareData';
import louisianaData from './louisianaData';

const stateData = {
  florida: {
    name: 'Florida',
    overview: {
      keyPoints: [
        'Florida follows the "best interests of the child" standard with 20 specific factors outlined in statute 61.13',
        'Shared parental responsibility is presumed unless proven detrimental to the child',
        'Florida abolished permanent alimony in 2023, focusing on bridge-the-gap and rehabilitative support',
        'Parenting plans are mandatory in all cases involving minor children',
        'Florida has a strong preference for time-sharing rather than traditional "visitation"',
        'The state requires parenting courses for divorcing parents with minor children'
      ],
      uniqueFeatures: [
        'Relocation statute requires 150-mile rule for major relocations',
        'Florida allows for "simplified dissolution" for uncontested divorces',
        'Domestic violence injunctions can be filed without an attorney',
        'Florida has specialized family drug courts in many counties'
      ]
    },
    legalRequirements: {
      residency: '6 months residency required before filing',
      filing: 'Must file in county where either spouse resides or where the family lived when separated',
      paternity: 'Unmarried fathers can establish paternity through acknowledgment, court order, or marriage',
      documentation: 'Florida requires financial affidavits in all family law cases',
      uniqueRules: [
        'Mandatory parenting course within 45 days of service',
        'Social investigation may be ordered in contested custody cases',
        'Collaborative divorce process is encouraged and supported by statute'
      ]
    },
    custodyTypes: {
      physicalCustody: 'Florida uses "time-sharing" terminology instead of physical custody',
      legalCustody: 'Called "parental responsibility" - shared responsibility is presumed',
      jointCustody: 'Shared parental responsibility with rotating or equal time-sharing',
      visitation: 'Referred to as "time-sharing schedule" with detailed parenting plans',
      specialProvisions: [
        'Supervised time-sharing may be ordered if safety concerns exist',
        'Virtual time-sharing (video calls) is increasingly recognized',
        'Grandparent rights are protected under specific circumstances'
      ]
    },
    parentingPlans: {
      required: true,
      elements: [
        'Detailed time-sharing schedule including weekdays, weekends, holidays, and summer',
        'Transportation arrangements and costs',
        'Communication methods between parents and with children',
        'Decision-making authority for education, healthcare, and extracurricular activities',
        'Methods for resolving disputes',
        'Any restrictions on either parent'
      ],
      tips: [
        'Be specific about pickup/drop-off times and locations',
        'Include provisions for make-up time if visits are missed',
        'Address how to handle schedule conflicts and changes',
        'Consider including technology use guidelines'
      ]
    },
    childSupport: {
      guidelines: 'Florida uses income shares model based on combined net income',
      factors: [
        'Both parents\' net income (after taxes and mandatory deductions)',
        'Number of children',
        'Cost of health insurance and childcare',
        'Other court-ordered support obligations',
        'Time-sharing schedule (substantial time-sharing can reduce support)'
      ],
      calculation: 'Support calculated using Florida\'s child support guidelines worksheet',
      modifications: 'Can be modified if substantial change in circumstances (15% change or $50/month)',
      enforcement: 'Florida has strong enforcement including license suspension, asset seizure, and contempt'
    },
    resources: {
      courts: [
        'Florida Courts Self-Help website with forms and instructions',
        'Family court self-help centers in most counties',
        'Certified family mediators available statewide',
        'Guardian ad litem programs in all circuits'
      ],
      support: [
        'Florida Department of Revenue Child Support Program',
        'Legal Aid organizations in all major cities',
        'Fathers\' rights groups including Florida Fathers Rights',
        'Parenting coordination services'
      ],
      legal: [
        'Florida Bar Family Law Section referrals',
        'Board-certified family law attorneys available',
        'Collaborative divorce professionals',
        'Domestic violence legal advocates'
      ],
      educational: [
        'Mandatory "Children First" parenting course',
        'Co-parenting communication classes',
        'Supervised visitation programs',
        'Domestic violence intervention programs'
      ]
    }
  },
  alabama: {
    name: 'Alabama',
    overview: {
      keyPoints: [
        'Alabama follows the "best interests of the child" standard with emphasis on moral character',
        'Joint custody is available but not presumed - must be requested and proven beneficial',
        'Alabama has strong preference for biological parents over third parties',
        'Fault-based grounds for divorce are still recognized and can affect custody',
        'Grandparent visitation rights are limited and require specific circumstances',
        'Alabama allows covenant marriages with stricter divorce requirements'
      ],
      uniqueFeatures: [
        'Alabama still considers adultery and other marital misconduct in custody decisions',
        'The state has a unique "tender years doctrine" history affecting young children',
        'Religious considerations can be factored into custody decisions',
        'Alabama recognizes common law marriage'
      ]
    },
    legalRequirements: {
      residency: '6 months residency in Alabama, 3 months in filing county',
      filing: 'Must file in county where defendant resides or where parties last lived together',
      paternity: 'Paternity can be established through acknowledgment, genetic testing, or presumption',
      documentation: 'Requires sworn financial statements and child support worksheets',
      uniqueRules: [
        'Mandatory waiting period of 30 days for uncontested divorces',
        'Corroborating witness may be required in uncontested cases',
        'Court may require counseling or mediation before granting divorce'
      ]
    },
    custodyTypes: {
      physicalCustody: 'Primary physical custody with visitation rights to non-custodial parent',
      legalCustody: 'Legal custody involves decision-making authority for major life decisions',
      jointCustody: 'Joint legal and physical custody available when parents can cooperate',
      visitation: 'Standard visitation includes alternating weekends and holiday schedules',
      specialProvisions: [
        'Supervised visitation ordered when child safety is a concern',
        'Split custody possible when multiple children are involved',
        'Extended visitation during summer months is common'
      ]
    },
    parentingPlans: {
      required: false,
      elements: [
        'Custody and visitation schedule',
        'Holiday and vacation arrangements',
        'Transportation responsibilities',
        'Communication guidelines',
        'Decision-making authority',
        'Child support arrangements'
      ],
      tips: [
        'Even though not required, detailed parenting plans help avoid conflicts',
        'Include specific provisions for school events and activities',
        'Address how to handle emergencies and medical decisions',
        'Consider including dispute resolution methods'
      ]
    },
    childSupport: {
      guidelines: 'Alabama uses Rule 32 child support guidelines based on income shares model',
      factors: [
        'Gross income of both parents',
        'Number of children',
        'Cost of health insurance premiums',
        'Extraordinary medical expenses',
        'Childcare costs necessary for employment',
        'Visitation transportation costs'
      ],
      calculation: 'Based on Alabama Rule 32 Child Support Guidelines worksheet',
      modifications: 'Material change in circumstances required, typically 10% change in income',
      enforcement: 'Alabama has strong enforcement including wage garnishment and asset seizure'
    },
    resources: {
      courts: [
        'Alabama Judicial System self-help resources',
        'Family court facilitators in larger counties',
        'Mediation programs available in most circuits',
        'Court-appointed attorneys for indigent parties'
      ],
      support: [
        'Alabama Department of Human Resources Child Support Division',
        'Legal Services Alabama for low-income individuals',
        'Alabama Center for Dispute Resolution',
        'Domestic violence shelters and advocates'
      ],
      legal: [
        'Alabama State Bar Lawyer Referral Service',
        'Family law specialists certified by Alabama Bar',
        'Pro bono programs in major cities',
        'Law school legal clinics'
      ],
      educational: [
        'Parenting education programs required in some counties',
        'Divorce education classes available',
        'Anger management and domestic violence programs',
        'Co-parenting counseling services'
      ]
    }
  },
  alaska: {
    name: 'Alaska',
    overview: {
      keyPoints: [
        'Alaska prioritizes joint custody arrangements and shared parenting responsibilities',
        'The state has unique provisions for Alaska Native custody issues and tribal considerations',
        'Alaska allows for informal dissolution of marriage in certain circumstances',
        'Domestic violence is taken very seriously with strong protective measures',
        'Alaska has special considerations for military families due to large military presence',
        'The state recognizes the challenges of distance in rural communities'
      ],
      uniqueFeatures: [
        'Alaska Native tribal law may apply in certain custody cases',
        'Special provisions for families in remote areas with limited transportation',
        'Military deployment considerations built into custody laws',
        'Alaska allows dissolution without formal divorce proceedings in some cases'
      ]
    },
    legalRequirements: {
      residency: 'Either spouse must be Alaska resident for 30 days before filing',
      filing: 'File in judicial district where either party resides',
      paternity: 'Paternity established through acknowledgment, court order, or genetic testing',
      documentation: 'Financial declarations required, simplified forms available',
      uniqueRules: [
        'No waiting period for divorce in Alaska',
        'Domestic violence cases receive expedited processing',
        'Special procedures for military deployment situations'
      ]
    },
    custodyTypes: {
      physicalCustody: 'Shared physical custody is preferred when feasible',
      legalCustody: 'Joint legal custody presumed unless contrary to child\'s best interests',
      jointCustody: 'Encouraged through mediation and collaborative processes',
      visitation: 'Flexible visitation arrangements considering Alaska\'s unique geography',
      specialProvisions: [
        'Long-distance visitation plans for rural families',
        'Seasonal considerations for travel and weather',
        'Special provisions for subsistence lifestyle families'
      ]
    },
    parentingPlans: {
      required: true,
      elements: [
        'Custody and visitation schedule with seasonal considerations',
        'Transportation arrangements considering Alaska\'s geography',
        'Communication methods including technology options',
        'Decision-making responsibilities',
        'Holiday schedules adapted for Alaska conditions',
        'Emergency contact procedures'
      ],
      tips: [
        'Plan for seasonal weather affecting travel',
        'Include backup communication methods for remote areas',
        'Consider subsistence activities in scheduling',
        'Address military deployment if applicable'
      ]
    },
    childSupport: {
      guidelines: 'Alaska uses modified income shares model with unique Alaska factors',
      factors: [
        'Adjusted annual income of both parents',
        'Number of children',
        'Cost of health insurance and extraordinary medical costs',
        'Childcare expenses',
        'Travel costs for visitation in Alaska\'s vast geography',
        'Cost of living adjustments for different regions'
      ],
      calculation: 'Alaska Child Support Guidelines with regional cost adjustments',
      modifications: 'Material change standard with consideration for Alaska-specific factors',
      enforcement: 'Strong enforcement including PFD (Permanent Fund Dividend) intercept'
    },
    resources: {
      courts: [
        'Alaska Court System self-help services',
        'Family law self-help centers in Anchorage and Fairbanks',
        'Video conferencing for remote court appearances',
        'Traveling court services for rural areas'
      ],
      support: [
        'Alaska Child Support Services Division',
        'Alaska Legal Services Corporation',
        'Domestic violence and sexual assault programs',
        'Military family support services'
      ],
      legal: [
        'Alaska Bar Association lawyer referral service',
        'Rural justice outreach programs',
        'Military legal assistance offices',
        'Alaska Native legal services'
      ],
      educational: [
        'Focus on Children divorce education program',
        'Co-parenting classes available statewide',
        'Domestic violence intervention programs',
        'Military family counseling services'
      ]
    }
  },
  nebraska: {
    name: 'Nebraska',
    overview: {
      keyPoints: [
        'Nebraska strongly favors joint physical custody and equal parenting time when possible',
        'The state uses "parenting time" terminology instead of traditional visitation',
        'Nebraska requires mandatory mediation for all contested custody cases',
        'Best interests standard includes 9 specific factors outlined in state statute',
        'Nebraska has a rebuttable presumption against awarding custody to domestic violence perpetrators',
        'The state allows for modification of parenting plans without court approval if both parents agree'
      ],
      uniqueFeatures: [
        'Nebraska\'s "2-2-3" rotation schedule is commonly used for shared custody arrangements',
        'The state has unique provisions for agricultural families considering seasonal work demands',
        'Nebraska allows parents to create their own parenting time schedules without court intervention',
        'Strong emphasis on maintaining children\'s relationships with extended family members'
      ]
    },
    legalRequirements: {
      residency: 'Child must have lived in Nebraska for 6 months before filing, or since birth if under 6 months',
      filing: 'File in county where child has lived for 6 months or where either parent resides',
      paternity: 'Paternity established through acknowledgment, genetic testing, court order, or marriage',
      documentation: 'Financial affidavit and parenting plan required in all cases involving children',
      uniqueRules: [
        'Mandatory mediation required before contested custody hearings',
        'Parent education classes required in all divorce cases with minor children',
        'Guardian ad litem appointment is common in contested custody cases'
      ]
    },
    custodyTypes: {
      physicalCustody: 'Nebraska prefers joint physical custody with equal or nearly equal parenting time',
      legalCustody: 'Joint legal custody is presumed unless evidence shows it\'s not in child\'s best interest',
      jointCustody: 'Strongly encouraged through 2-2-3 schedules, alternating weeks, or other equal-time arrangements',
      visitation: 'Called "parenting time" - minimum every other weekend plus one weeknight is standard baseline',
      specialProvisions: [
        'Supervised parenting time available through court-approved programs',
        'Virtual parenting time increasingly recognized for long-distance situations',
        'Extended summer parenting time typically 4-6 weeks for non-residential parent'
      ]
    },
    parentingPlans: {
      required: true,
      elements: [
        'Detailed parenting time schedule including regular rotation, holidays, and summer',
        'Transportation arrangements and responsibility for costs',
        'Communication guidelines between parents and with children',
        'Decision-making authority for medical, educational, and religious matters',
        'Dispute resolution procedures including mediation requirements',
        'Provisions for relocation and travel with children'
      ],
      tips: [
        'Nebraska courts favor detailed, specific schedules over general arrangements',
        'Include provisions for makeup time when parenting time is missed',
        'Address how to handle children\'s extracurricular activities and school events',
        'Consider seasonal variations for agricultural or weather-dependent work schedules'
      ]
    },
    childSupport: {
      guidelines: 'Nebraska uses income shares model based on both parents\' net monthly income',
      factors: [
        'Net monthly income of both parents after taxes and mandatory deductions',
        'Number of children covered by the order',
        'Cost of health insurance premiums for the children',
        'Childcare expenses necessary for employment or education',
        'Parenting time percentage (significant time can reduce support obligation)',
        'Special needs of the children requiring additional expenses'
      ],
      calculation: 'Nebraska Child Support Guidelines using standardized worksheets and tables',
      modifications: 'Material and substantial change in circumstances, typically 10% change in income or support amount',
      enforcement: 'Strong enforcement including wage garnishment, tax intercept, license suspension, and asset seizure'
    },
    resources: {
      courts: [
        'Nebraska Supreme Court Self-Help Center',
        'Nebraska Judicial Branch - Family Court Services',
        'Douglas County Family Court',
        'Lancaster County District Court - Family Division',
        'Nebraska Court Forms',
        'Nebraska Child Support Calculator'
      ],
      support: [
        'Nebraska Child Support Payment Center',
        'Legal Aid of Nebraska',
        'Nebraska State Bar Association Family Law Section',
        'Nebraska Domestic Violence Hotline',
        'Nebraska Department of Health and Human Services - Child Support Enforcement'
      ],
      legal: [
        'Nebraska State Bar Lawyer Referral Service',
        'Volunteer Lawyers Project',
        'University of Nebraska College of Law Legal Clinic',
        'Creighton University Law School Legal Clinic',
        'Nebraska Collaborative Divorce Professionals'
      ],
      educational: [
        'Focus on Children Program',
        'Nebraska Parenting Act Resources',
        'Mediation Centers of Nebraska',
        'Parents Forever Program',
        'Nebraska Children\'s Commission'
      ]
    }
  },
  // Merge all state data
  ...maineData,
  ...wyomingData,
  ...wisconsinData,
  ...westVirginiaData,
  ...washingtonDCData,
  ...washingtonStateData,
  ...virginiaData,
  ...vermontData,
  ...utahData,
  ...texasData,
  ...tennesseeData,
  ...californiaData,
  ...iowaData,
  ...arizonaData,
  ...coloradoData,
  ...arkansasData,
  ...connecticutData,
  ...delawareData,
  ...louisianaData
};

export default stateData;